import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  FluentProvider,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Tab,
  TabList,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";
import {
  AlertOn20Regular,
  ArrowClockwise20Regular,
  ArrowLeft20Regular,
  Checkmark20Regular,
  Code20Regular,
  ErrorCircle20Regular,
  Folder20Regular,
  Home20Regular,
  ShieldLock20Regular,
} from "@fluentui/react-icons";
import { api, ApiError, initializeTeams, reviewLimitSessionId } from "./api";
import { loadDashboard } from "./dashboard";

type TabKey = "overview" | "notifications" | "sentry" | "github" | "repositories" | "breakglass";
type Principal = { email: string; name: string; roles: string[] };
type Notification = {
  id: string; source: string; project: string; severity: string; title: string;
  facts: Record<string, string>; timestamp: string; deliveryStatus: string;
};
type SentryIssue = {
  id: string; shortId: string; title: string; culprit: string; level: string; status: string;
  count: string; userCount: number; lastSeen: string; permalink: string;
  project: { name: string; slug: string }; assignedTo: { id: string; name: string } | null;
};
export type Gate = {
  repository: string; pullNumber: number; title: string; url: string; author: string; baseBranch: string;
  headSha: string; baseSha: string | null; approvedBy: string | null; gateStatus: string; gateSummary: string; updatedAt: string;
};
type GateFilter = "all" | "start" | "review" | "fix" | "approval" | "merge" | "failure";
type PullRequestDetails = {
  repository: string; pullNumber: number; title: string; url: string; state: string; draft: boolean; body: string;
  author: string; baseBranch: string; baseSha: string; headBranch: string; headSha: string;
  createdAt: string; updatedAt: string; mergeableState: string; commits: number; additions: number; deletions: number;
  changedFiles: number; comments: number; reviewComments: number; labels: string[]; assignees: string[];
  requestedReviewers: string[];
  flowStage: string; flowSummary: string; nextAction: "start" | "approve" | "merge" | null;
  reviewerName: string | null;
  codexReviewState: string; codexReviewSha: string | null; reviewFeedback: string[];
  reviewsTruncated: boolean;
  reviews: Array<{ id: number; author: string; state: string; submittedAt: string | null }>;
  checksTruncated: boolean;
  checks: Array<{ id: number; name: string; status: string; conclusion: string | null; detailsUrl: string | null }>;
};
type AdoBlueprint = { repository: string; commit: string; version: string; files: Array<{ path: string; bytes: number }> };
type AdoIngress = {
  enabled: boolean;
  subscriptions: Array<{ id: string; eventType: string; createdAt: string; createdBy: string | null; rollbackError: string | null }>;
  deliveries: Array<{ eventId: string; eventType: string; repositoryId: string | null; resourceId: string | null; status: string; outcome: string | null; receivedAt: string }>;
};
type AdoTracking = { findingKey: string; kind: string; title: string; status: string; workItemId: number | null; workItemUrl: string | null; error: string | null; updatedAt: string };
type AdoStatus = { actors: Array<{ id: string; configured: boolean; enabled: boolean }>; connection: { organization: string; project: { id: string; name: string }; visibleProjects: string[] } };
type AdoProbe = { subscriptionId: string; owner: string | null; consumerUrl: string; notification: { id: number; status: string | null; result: string | null; httpStatus: number | null; response: string | null }; deliveredAt: string; deleted: boolean };
type BreakglassOverview = { sessions: any[]; technicians: any[]; servers: any[]; audit: any[] };
type OnboardingJob = {
  id: string; repository: string; profile: string; capabilities: string[]; description: string; team: string; teamPermission: string; topics: string[];
  classification: string | null; status: string; currentStep: string; error: string | null; updatedAt: string;
  migrationBaselines?: Array<{
    id: string; route: string; status: string; sourceSha: string; targetSha: string; diffFingerprint: string;
    pullNumber: number | null; mergeSha: string | null; invalidReason: string | null;
  }>;
};
type ManagedRepository = {
  repository: string; profile: string; capabilities: string[]; desiredBlueprintCommit: string; appliedBlueprintCommit: string | null;
  blueprintVersion: string; updatePullNumber: number | null; status: string; drift: string[]; lastError: string | null; updatedAt: string;
};
type ProtectedBranchTip = {
  repository: string; branch: "dev" | "staging" | "main"; tipSha: string;
  status: "present" | "deleted" | "blocked"; initializedAt: string; updatedAt: string;
};
type ReviewLimitSession = {
  sessionId: string;
  repository: string;
  branch: string;
  round: number;
  purpose: "round_limit" | "reviewer_fallback";
  authorTool: "codex" | "claude" | null;
  reviewerTool: "codex" | "claude" | null;
  findings: { critical: number; high: number; medium: number; low: number; recent: string[] };
  status: "pending" | "continue" | "stop" | "expired";
  expiresAt: string;
};

const NAV: Array<{ key: TabKey; label: string; icon: ReactElement }> = [
  { key: "overview", label: "Übersicht", icon: <Home20Regular /> },
  { key: "notifications", label: "Benachrichtigungen", icon: <AlertOn20Regular /> },
  { key: "sentry", label: "Sentry", icon: <ErrorCircle20Regular /> },
  { key: "github", label: "GitHub", icon: <Code20Regular /> },
  { key: "repositories", label: "Repositories", icon: <Folder20Regular /> },
  { key: "breakglass", label: "Breaking Glass", icon: <ShieldLock20Regular /> },
];

function relative(value: string): string {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("de", { numeric: "auto" });
  if (Math.abs(seconds) < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (Math.abs(seconds) < 86400) return formatter.format(Math.round(seconds / 3600), "hour");
  return formatter.format(Math.round(seconds / 86400), "day");
}

function Status({ value, label = value }: { value: string; label?: string }) {
  const color = ["success", "delivered", "approved", "used", "ready_to_merge", "clean", "managed", "completed"].includes(value) ? "success"
    : ["critical", "failure", "failed", "blocked", "deleted", "denied", "rejected", "changes_requested", "review_fix_required"].includes(value) ? "danger"
      : ["warning", "waiting", "pending", "queued", "running", "drift", "update_pending", "in_progress", "action_required", "start_required", "review_pending", "ci_pending", "approval_required"].includes(value) ? "warning" : "informative";
  return <Badge appearance="tint" color={color}>{label}</Badge>;
}

function Empty({ children }: { children: string }) {
  return <div className="empty" tabIndex={-1}><span className="empty-mark">IT</span><h3>Noch nichts zu sehen</h3><p>{children}</p></div>;
}

export function App() {
  const [tab, setTab] = useState<TabKey>((location.hash.slice(2) as TabKey) || "overview");
  const [dark, setDark] = useState(false);
  const [access, setAccess] = useState<"checking" | "ready" | "blocked">("checking");
  const [me, setMe] = useState<Principal | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [sentry, setSentry] = useState<SentryIssue[]>([]);
  const [gates, setGates] = useState<Gate[]>([]);
  const [onboardings, setOnboardings] = useState<OnboardingJob[]>([]);
  const [managedRepositories, setManagedRepositories] = useState<ManagedRepository[]>([]);
  const [protectedBranchTips, setProtectedBranchTips] = useState<ProtectedBranchTip[]>([]);
  const [breakglass, setBreakglass] = useState<BreakglassOverview | null>(null);
  const [ado, setAdo] = useState<AdoStatus | string | null>(null);
  const [adoProbe, setAdoProbe] = useState<AdoProbe | null>(null);
  const [adoBlueprint, setAdoBlueprint] = useState<AdoBlueprint | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [reviewLimitId, setReviewLimitId] = useState<string | null>(null);

  const operator = me?.roles.includes("Operator") ?? false;

  // Azure DevOps status costs ADO requests (TSTU) per call: loaded when the overview opens or on click, never in the
  // 15-second refresh (AB#98).
  const loadAdo = useCallback(async () => {
    try { setAdo(await api<AdoStatus>("/api/ado/status")); }
    catch (current) { setAdo(current instanceof Error ? current.message : "nicht erreichbar"); }
  }, []);

  const load = useCallback(async () => {
    setError(null);
    setInfo(null);
    try {
      const data = await loadDashboard<Principal, Notification, Gate, OnboardingJob, ManagedRepository, ProtectedBranchTip, BreakglassOverview>(api);
      setMe(data.principal); setNotifications(data.notifications); setGates(data.gates); setBreakglass(data.breakglass);
      setOnboardings(data.onboardings); setManagedRepositories(data.repositories); setProtectedBranchTips(data.branchTips);
      setInfo(data.notConfigured.length ? `In dieser Umgebung nicht konfiguriert: ${data.notConfigured.join(", ")}.` : null);
      if (data.failed.length) setError(`Nicht vollständig geladen: ${data.failed.join(", ")}`);
    } catch (current) {
      setError(current instanceof Error ? current.message : "Unbekannter Fehler");
    }
  }, []);

  const loadSentry = useCallback(async () => {
    try { setSentry(await api<SentryIssue[]>("/api/sentry/issues")); }
    catch (current) { setError(current instanceof Error ? current.message : "Sentry konnte nicht geladen werden"); }
  }, []);

  useEffect(() => {
    // Zweiter then-Parameter statt catch: nur ein fehlgeschlagenes initializeTeams() blockiert.
    // API-Fehler bleiben Fehler in der App und zeigen ihre eigene Meldung.
    initializeTeams().then(
      async ({ theme, subPageId }) => {
        setDark(theme === "dark" || theme === "contrast");
        setReviewLimitId(reviewLimitSessionId(subPageId));
        await load();
        setAccess("ready");
      },
      () => setAccess("blocked"),
    );
  }, [load]);

  useEffect(() => { if (access === "ready" && tab === "sentry" && sentry.length === 0) void loadSentry(); }, [access, tab, sentry.length, loadSentry]);
  useEffect(() => { if (access === "ready" && tab === "overview") void loadAdo(); }, [access, tab, loadAdo]);
  useEffect(() => {
    if (access !== "ready") return;
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [access, load]);

  const chooseTab = (key: TabKey) => { setReviewLimitId(null); setTab(key); location.hash = `/${key}`; setSearch(""); };
  const act = async (id: string, action: () => Promise<unknown>, success: string, refresh: () => Promise<void> = load) => {
    setBusy(id); setError(null);
    try { await action(); setNotice(success); await refresh(); window.setTimeout(() => setNotice(null), 2400); }
    catch (current) { setError(current instanceof Error ? current.message : "Aktion fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  const filteredNotifications = useMemo(() => notifications.filter((item) =>
    `${item.title} ${item.project} ${item.source}`.toLowerCase().includes(search.toLowerCase())), [notifications, search]);

  if (access === "checking") return <FluentProvider theme={dark ? webDarkTheme : webLightTheme}><div className="center"><Spinner label="InfraTeams wird geladen …" /></div></FluentProvider>;
  if (access === "blocked") return <FluentProvider theme={webLightTheme}><div className="restricted" role="main"><span className="brand-mark">IT</span><h1>Nur in Microsoft Teams verfügbar</h1><p>Öffnen Sie InfraTeams über die installierte App in Microsoft Teams.</p></div></FluentProvider>;

  return <FluentProvider theme={dark ? webDarkTheme : webLightTheme} className={dark ? "theme-dark" : "theme-light"}>
    <div className="app-shell">
      <header>
        <div className="brand"><span className="brand-mark">IT</span><div><strong>InfraTeams</strong><small>Operations Hub</small></div></div>
        <div className="header-actions">
          <Button appearance="subtle" icon={<ArrowClockwise20Regular />} onClick={() => void load()} aria-label="Aktualisieren" />
          <button className="theme-toggle" onClick={() => setDark((value) => !value)} aria-label="Farbschema wechseln"><span /></button>
          <div className="identity"><span>{me?.name ?? "Nicht angemeldet"}</span><small>{operator ? "Operator" : "Reader"}</small></div>
          <span className="avatar">{(me?.name ?? "IT").split(" ").map((part) => part[0]).slice(0, 2).join("")}</span>
        </div>
      </header>
      <nav aria-label="Hauptnavigation">
        <TabList selectedValue={tab} onTabSelect={(_, data) => chooseTab(data.value as TabKey)}>
          {NAV.map((item) => <Tab key={item.key} value={item.key} icon={item.icon}>{item.label}</Tab>)}
        </TabList>
      </nav>
      <main>
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        {notice && <MessageBar intent="success"><MessageBarBody>{notice}</MessageBarBody></MessageBar>}
        {info && <MessageBar intent="info"><MessageBarBody>{info}</MessageBarBody></MessageBar>}
        {reviewLimitId
          ? <ReviewLimitView sessionId={reviewLimitId} operator={operator} onClose={() => setReviewLimitId(null)} />
          : <>
            {tab === "overview" && <Overview notifications={notifications} gates={gates} breakglass={breakglass} onNavigate={chooseTab} />}
            {tab === "overview" && ado !== null && <AdoPanel status={ado} probe={adoProbe} blueprint={adoBlueprint} operator={operator} busy={busy} refresh={() => void loadAdo()} readBlueprint={() => act("ado-blueprint", async () => { setAdoBlueprint(null); setAdoBlueprint(await api<AdoBlueprint>("/api/ado/blueprint")); }, "ProjectBlueprint aus Platform gelesen")} run={() => act("ado-probe", async () => { setAdoProbe(null); setAdoProbe(await api<AdoProbe>("/api/ado/service-hooks/probe", { method: "POST", body: "{}" })); }, "Service-Hook-Probe erfolgreich: Zustellung in Dev empfangen")} />}
            {tab === "overview" && ado !== null && typeof ado !== "string" && <AdoIngressPanel operator={operator} busy={busy} act={act} />}
            {tab === "overview" && <TeamsAppPanel operator={operator} busy={busy} act={act} />}
            {tab === "notifications" && <Notifications items={filteredNotifications} search={search} setSearch={setSearch} />}
            {tab === "sentry" && <SentryView items={sentry} operator={operator} busy={busy} search={search} setSearch={setSearch} act={act} reload={loadSentry} me={me} />}
            {tab === "github" && <GithubView items={gates} operator={operator} busy={busy} act={act} reload={load} />}
            {tab === "repositories" && <RepositoriesView jobs={onboardings} repositories={managedRepositories} branchTips={protectedBranchTips} operator={operator} busy={busy} act={act} reload={load} />}
            {tab === "breakglass" && <BreakglassView data={breakglass} operator={operator} busy={busy} act={act} />}
          </>}
      </main>
    </div>
  </FluentProvider>;
}

function ReviewLimitView({ sessionId, operator, onClose }: { sessionId: string; operator: boolean; onClose: () => void }) {
  const [session, setSession] = useState<ReviewLimitSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setSession(await api<ReviewLimitSession>(`/api/review-limit/sessions/${sessionId}`));
      setError(null);
    } catch (current) {
      setError(current instanceof Error ? current.message : "Review-Freigabe konnte nicht geladen werden");
    }
  }, [sessionId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (session?.status !== "pending") return;
    const timer = window.setInterval(() => void load(), 5_000);
    return () => window.clearInterval(timer);
  }, [load, session?.status]);

  const decide = async (decision: "continue" | "stop") => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api<{ status: ReviewLimitSession["status"]; changed: boolean }>("/api/review-limit/sessions/decision", {
        method: "POST",
        body: JSON.stringify({ sessionId, decision }),
      });
      setNotice(result.changed
        ? decision === "continue"
          ? session?.purpose === "reviewer_fallback" ? "Der Fallback-Review wurde freigegeben." : "Eine weitere Review-Runde wurde freigegeben."
          : "Der Cross-Review wurde gestoppt."
        : "Die Entscheidung wurde bereits auf einem anderen Gerät getroffen.");
      await load();
    } catch (current) {
      setError(current instanceof Error ? current.message : "Entscheidung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return <section>
    <Button className="repo-back" appearance="subtle" icon={<ArrowLeft20Regular />} onClick={onClose}>Zur Übersicht</Button>
    <div className="page-heading"><div><p className="eyebrow">Cross-Review</p><h1>{session?.purpose === "reviewer_fallback" ? "Fallback-Review" : "Weitere Review-Runde"}</h1><p>Die erste Entscheidung gilt auf allen Geräten.</p></div></div>
    {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
    {notice && <MessageBar intent="success"><MessageBarBody>{notice}</MessageBarBody></MessageBar>}
    {!session ? <div className="center-panel"><Spinner label="Review-Freigabe wird geladen …" /></div> : <Card className="session-card review-limit-card">
      <div className="row-title"><strong>{session.repository}</strong><Status value={session.status} label={session.status === "pending" ? "Entscheidung offen" : session.status === "continue" ? "Freigegeben" : session.status === "stop" ? "Gestoppt" : "Abgelaufen"} /></div>
      <dl>
        <div><dt>Branch</dt><dd>{session.branch}</dd></div>
        <div><dt>Runde</dt><dd>{session.round}</dd></div>
        {session.purpose === "reviewer_fallback" && <div><dt>Fallback</dt><dd>{session.authorTool} prüft in separater Sitzung</dd></div>}
        <div><dt>Findings</dt><dd>{session.findings.critical + session.findings.high + session.findings.medium + session.findings.low}</dd></div>
        <div><dt>Ablauf</dt><dd>{new Date(session.expiresAt).toLocaleString("de-DE")}</dd></div>
      </dl>
      <div className="review-limit-counts">
        <Status value={session.findings.critical ? "critical" : "success"} label={`Critical ${session.findings.critical}`} />
        <Status value={session.findings.high ? "failure" : "success"} label={`High ${session.findings.high}`} />
        <Status value={session.findings.medium ? "warning" : "success"} label={`Medium ${session.findings.medium}`} />
        <Status value="informative" label={`Low ${session.findings.low}`} />
      </div>
      {session.findings.recent.length > 0 && <div className="review-feedback">{session.findings.recent.map((finding) => <p key={finding}>{finding}</p>)}</div>}
      {session.status === "pending" && operator && <div className="actions review-limit-actions">
        <Button appearance="primary" disabled={busy} onClick={() => void decide("continue")}>{session.purpose === "reviewer_fallback" ? "Fallback-Review freigeben" : "Eine Runde fortsetzen"}</Button>
        <Button disabled={busy} onClick={() => void decide("stop")}>Stoppen</Button>
      </div>}
    </Card>}
  </section>;
}

function Overview({ notifications, gates, breakglass, onNavigate }: { notifications: Notification[]; gates: Gate[]; breakglass: BreakglassOverview | null; onNavigate: (tab: TabKey) => void }) {
  const pendingSessions = breakglass?.sessions.filter((item) => item.status === "pending").length ?? 0;
  const blocked = gates.filter((gate) => gate.gateStatus !== "success").length;
  const critical = notifications.filter((item) => item.severity === "critical").length;
  return <section>
    <div className="page-heading"><div><p className="eyebrow">Operations Center</p><h1>Guten Überblick behalten.</h1><p>Freigaben, Deployments und Incidents an einem Ort.</p></div></div>
    <div className="stats">
      <button className="stat-card" onClick={() => onNavigate("breakglass")}><small>Offene Freigaben</small><strong>{breakglass ? pendingSessions : "–"}</strong><span>{breakglass ? "Breaking Glass" : "Breaking Glass nicht verfügbar"}</span></button>
      <button className="stat-card" onClick={() => onNavigate("github")}><small>Blockierte Merges</small><strong>{blocked}</strong><span>Geschützte Branches</span></button>
      <button className="stat-card" onClick={() => onNavigate("notifications")}><small>Kritische Events</small><strong>{critical}</strong><span>Letzte 90 Tage</span></button>
      <div className="stat-card"><small>Registrierte Server</small><strong>{breakglass ? breakglass.servers.length : "–"}</strong><span>{breakglass ? "Notfallzugriff" : "nicht verfügbar"}</span></div>
    </div>
    <div className="section-heading"><div><h2>Neueste Signale</h2><p>Die letzten systemübergreifenden Ereignisse.</p></div></div>
    <EventList items={notifications.slice(0, 8)} />
  </section>;
}

// ADO migration, sprint 2: shows that the worker talks to Azure DevOps as service principal and lets an Operator
// prove the service hook rights (create, test delivery, delete) in the environment's own project.
function AdoPanel({ status, probe, blueprint, operator, busy, refresh, readBlueprint, run }: { status: AdoStatus | string; probe: AdoProbe | null; blueprint: AdoBlueprint | null; operator: boolean; busy: string | null; refresh: () => void; readBlueprint: () => void; run: () => void }) {
  return <section className="ado-panel">
    <div className="section-heading"><div><h2>Azure DevOps</h2><p>Verbindung des Service Principals infrateams-worker.</p></div></div>
    {typeof status === "string"
      ? <MessageBar intent="warning"><MessageBarBody>Azure DevOps: {status}</MessageBarBody></MessageBar>
      : <SimpleTable columns={["Organisation", "Projekt", "Sichtbare Projekte", "Aktor"]} rows={[[status.connection.organization, status.connection.project.name, status.connection.visibleProjects.join(", "), status.actors.map((actor) => `${actor.id}: ${actor.enabled ? "aktiv" : "gesperrt"}`).join(", ")]]} />}
    <div className="actions"><Button size="small" onClick={refresh}>Aktualisieren</Button><Button size="small" disabled={!operator || typeof status === "string" || busy === "ado-probe"} onClick={run}>Service-Hook-Probe</Button><Button size="small" disabled={typeof status === "string" || busy === "ado-blueprint"} onClick={readBlueprint}>ProjectBlueprint lesen</Button></div>
    {blueprint && <SimpleTable columns={["Quelle", "Commit", "Version", "Dateien"]} rows={[[blueprint.repository, blueprint.commit.slice(0, 12), blueprint.version, blueprint.files.map((file) => `${file.path} (${file.bytes} B)`).join(", ")]]} />}
    {probe && <SimpleTable columns={["Subscription", "Eigentümer", "Ziel", "Zustellung", "Antwort", "Empfangen in Dev", "Gelöscht"]} rows={[[probe.subscriptionId, probe.owner ?? "–", probe.consumerUrl, `${probe.notification.status ?? "–"} / ${probe.notification.result ?? "–"}`, probe.notification.httpStatus ?? probe.notification.response ?? "–", relative(probe.deliveredAt), probe.deleted ? "ja" : "nein"]]} />}
  </section>;
}

// ADO migration, sprint 3: ingress subscriptions (create/rotate, remove), recent deliveries and the explicit ledger
// initialization of a test repository in the environment's own project.
function AdoIngressPanel({ operator, busy, act }: { operator: boolean; busy: string | null; act: (id: string, action: () => Promise<unknown>, success: string, refresh?: () => Promise<void>) => Promise<void> }) {
  const [ingress, setIngress] = useState<AdoIngress | string | null>(null);
  const [tracking, setTracking] = useState<AdoTracking[]>([]);
  const [repository, setRepository] = useState("");
  const reload = useCallback(async () => {
    try { setIngress(await api<AdoIngress>("/api/ado/ingress")); setTracking(await api<AdoTracking[]>("/api/ado/tracking")); }
    catch (current) { setIngress(current instanceof Error ? current.message : "nicht erreichbar"); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  if (ingress === null) return null;
  if (typeof ingress === "string") return <MessageBar intent="warning"><MessageBarBody>Azure-DevOps-Eingang: {ingress}</MessageBarBody></MessageBar>;
  return <section className="ado-panel">
    <div className="section-heading"><div><h2>Azure-DevOps-Eingang</h2><p>{ingress.enabled ? "Service Hooks auf /webhooks/azure-devops mit statischem Secret-Header." : "In dieser Umgebung nicht aktiv."}</p></div></div>
    {ingress.subscriptions.some((item) => item.rollbackError) && <MessageBar intent="error"><MessageBarBody>Rückbau offen: Eine Subscription aus einer abgebrochenen Rotation besteht noch. „Eingang entfernen“ oder erneut rotieren.</MessageBarBody></MessageBar>}
    <SimpleTable columns={["Subscription", "Ereignis", "Angelegt", "Eigentümer", "Rückbau"]} rows={ingress.subscriptions.map((item) => [item.id, item.eventType, relative(item.createdAt), item.createdBy ?? "–", item.rollbackError ? `offen: ${item.rollbackError}` : "–"])} />
    <div className="actions">
      <Button size="small" onClick={() => void reload()}>Aktualisieren</Button>
      <Button size="small" disabled={!operator || !ingress.enabled || busy === "ado-ingress"} onClick={() => act("ado-ingress", () => api("/api/ado/ingress/subscriptions", { method: "POST", body: "{}" }), ingress.subscriptions.length ? "Eingang rotiert: neue Secrets, alte Subscriptions gelöscht" : "Eingang eingerichtet", reload)}>{ingress.subscriptions.length ? "Secrets rotieren" : "Eingang einrichten"}</Button>
      <Button size="small" disabled={!operator || !ingress.subscriptions.length || busy === "ado-ingress-remove"} onClick={() => act("ado-ingress-remove", () => api("/api/ado/ingress/subscriptions/remove", { method: "POST", body: "{}" }), "Eingang entfernt", reload)}>Eingang entfernen</Button>
    </div>
    <div className="actions">
      <Input aria-label="Test-Repository" placeholder="Test-Repository im eigenen Projekt" value={repository} onChange={(_, data) => setRepository(data.value)} />
      <Button size="small" disabled={!operator || !repository || busy === "ado-ledger"} onClick={() => act("ado-ledger", () => api("/api/ado/ledger/initialize", { method: "POST", body: JSON.stringify({ repository }) }), `Ledger für ${repository} initialisiert`)}>Ledger initialisieren</Button>
      <Button size="small" disabled={!operator || !repository || busy === "ado-ledger-recheck"} onClick={() => act("ado-ledger-recheck", () => api("/api/ado/ledger/recheck", { method: "POST", body: JSON.stringify({ repository }) }), `Ledger für ${repository} neu geprüft`)}>Ledger neu prüfen</Button>
    </div>
    <SimpleTable columns={["Empfangen", "Ereignis", "Repository", "Ressource", "Status", "Ergebnis"]} rows={ingress.deliveries.map((item) => [relative(item.receivedAt), item.eventType, item.repositoryId ?? "–", item.resourceId ?? "–", item.status, item.outcome ?? "–"])} />
    <h3>Befunde (Tracking)</h3>
    <SimpleTable columns={["Befund", "Titel", "Work Item", "Status", "Aktualisiert"]} rows={tracking.map((item) => [item.kind, item.title, item.workItemId ? `#${item.workItemId}` : item.error ?? "–", item.status, relative(item.updatedAt)])} />
  </section>;
}

// ADO migration, sprint 3 (AB#102): bring personal installations of this environment's Teams app to the shipped
// version via Microsoft Graph (the environment's own Entra app, no user credential).
type TeamsAppUpgrade = {
  target: { externalId: string; version: string }; catalogVersion: string | null; aborted: string | null; scope?: string; verification?: "verified" | "none";
  users: { processed: number; selected: number; total: number; limited: boolean };
  results: Array<{ userId: string; action?: string; installedVersion?: string | null; actualVersion?: string | null; targetVersion?: string; error?: string }>;
};
function TeamsAppPanel({ operator, busy, act }: { operator: boolean; busy: string | null; act: (id: string, action: () => Promise<unknown>, success: string, refresh?: () => Promise<void>) => Promise<void> }) {
  const [target, setTarget] = useState<{ externalId: string; version: string } | null>(null);
  const [result, setResult] = useState<TeamsAppUpgrade | null>(null);
  useEffect(() => { api<{ externalId: string; version: string }>("/api/teams-app").then(setTarget, () => setTarget(null)); }, []);
  if (!target) return null;
  const run = (scope: "me" | "known") => act(`teams-app-${scope}`, async () => {
    try {
      setResult(await api<TeamsAppUpgrade>("/api/teams-app/upgrade", { method: "POST", body: JSON.stringify({ scope }) }));
    } catch (error) {
      // An aborted run (502) still reports what was done: show those results, then raise the error message.
      if (error instanceof ApiError && error.status === 502 && error.body && typeof error.body === "object" && "results" in error.body) setResult(error.body as TeamsAppUpgrade);
      throw error;
    }
  }, "Teams-App-Installationen geprüft", async () => undefined);
  return <section className="ado-panel">
    <div className="section-heading"><div><h2>Teams-App</h2><p>Ausgelieferte Version {target.version}. Hebt persönliche Installationen auf diese Version.</p></div></div>
    <div className="actions">
      <Button size="small" disabled={!operator || busy === "teams-app-me"} onClick={() => void run("me")}>Meine Installation aktualisieren</Button>
      <Button size="small" disabled={!operator || busy === "teams-app-known"} onClick={() => void run("known")}>Alle bekannten Nutzer</Button>
    </div>
    {result?.scope === "me" && result.verification === "none" && result.results[0]?.action === "current" && <MessageBar intent="info"><MessageBarBody>bereits aktuell, keine Verifikation – Freigabe für alle erst nach einem Upgrade-Lauf</MessageBarBody></MessageBar>}
    {result?.users.limited && <MessageBar intent="warning"><MessageBarBody>{result.users.total} bekannte Nutzer, verarbeitet wurden nur die ersten {result.users.selected}.</MessageBarBody></MessageBar>}
    {result && <SimpleTable columns={["Nutzer", "Ergebnis", "Vorher", "Jetzt installiert", "Ziel"]} rows={result.results.map((item) => [item.userId, item.action ?? item.error ?? "–", item.installedVersion ?? "–", item.actualVersion ?? "–", item.targetVersion ?? result.target.version])} />}
  </section>;
}

function Notifications({ items, search, setSearch }: { items: Notification[]; search: string; setSearch: (value: string) => void }) {
  return <section><PageHeading eyebrow="Event Stream" title="Benachrichtigungen" description="Zentral erfasst, über Cloudflare Queues zugestellt." search={search} setSearch={setSearch} />
    <EventList items={items} />
  </section>;
}

function EventList({ items }: { items: Notification[] }) {
  if (!items.length) return <Empty>Noch keine passenden Ereignisse eingegangen.</Empty>;
  return <div className="data-list">{items.map((item) => <article className="data-row" key={item.id}>
    <span className={`signal ${item.severity}`} />
    <div className="grow"><div className="row-title"><strong>{item.title}</strong><Status value={item.severity} /></div><p>{item.project} · {item.source}</p></div>
    <div className="row-meta"><Status value={item.deliveryStatus} /><time>{relative(item.timestamp)}</time></div>
  </article>)}</div>;
}

function SentryView({ items, operator, busy, search, setSearch, act, reload, me }: any) {
  const filtered = items.filter((item: SentryIssue) => `${item.title} ${item.project.name} ${item.shortId}`.toLowerCase().includes(search.toLowerCase()));
  return <section><PageHeading eyebrow="Error Operations" title="Sentry" description="Offene Issues aus allen Projekten der Organisation." search={search} setSearch={setSearch} />
    {!filtered.length ? <Empty>Keine offenen Sentry-Issues gefunden.</Empty> : <div className="data-list">{filtered.map((issue: SentryIssue) => <article className="data-row action-row" key={issue.id}>
      <span className={`signal ${issue.level === "error" || issue.level === "fatal" ? "critical" : "warning"}`} />
      <div className="grow"><div className="row-title"><a href={issue.permalink} target="_blank" rel="noreferrer"><strong>{issue.title}</strong></a><Status value={issue.level} /></div><p>{issue.project.name} · {issue.shortId} · {issue.count} Events · {issue.assignedTo?.name ?? "nicht zugewiesen"}</p></div>
      <div className="actions"><Button size="small" disabled={!operator || busy === issue.id} onClick={() => act(issue.id, () => api("/api/sentry/actions", { method: "POST", body: JSON.stringify({ issueId: issue.id, action: "assign", assignee: me.email }) }), "Issue zugewiesen", reload)}>Mir zuweisen</Button>{issue.status === "unresolved" ? <><Button size="small" appearance="primary" disabled={!operator || busy === issue.id} onClick={() => act(issue.id, () => api("/api/sentry/actions", { method: "POST", body: JSON.stringify({ issueId: issue.id, action: "resolve" }) }), "Issue gelöst", reload)}>Lösen</Button><Button size="small" disabled={!operator || busy === issue.id} onClick={() => act(issue.id, () => api("/api/sentry/actions", { method: "POST", body: JSON.stringify({ issueId: issue.id, action: "ignore" }) }), "Issue ignoriert", reload)}>Ignorieren</Button></> : <Button size="small" appearance="primary" disabled={!operator || busy === issue.id} onClick={() => act(issue.id, () => api("/api/sentry/actions", { method: "POST", body: JSON.stringify({ issueId: issue.id, action: "reopen" }) }), "Issue wieder geöffnet", reload)}>Wieder öffnen</Button>}</div>
    </article>)}</div>}
  </section>;
}

const FLOW_FILTERS: Record<Exclude<GateFilter, "all">, string[]> = {
  start: ["start_required"],
  review: ["review_pending", "ci_pending", "waiting", "action_required"],
  fix: ["review_fix_required"],
  approval: ["approval_required"],
  merge: ["ready_to_merge", "success"],
  failure: ["failure", "base_update_required"],
};

export function groupGatesByRepository(items: Gate[], filter: GateFilter): Array<[string, Gate[]]> {
  const groups = new Map<string, Gate[]>();
  for (const gate of items) {
    if (filter !== "all" && !FLOW_FILTERS[filter].includes(gate.gateStatus)) continue;
    const group = groups.get(gate.repository);
    if (group) group.push(gate);
    else groups.set(gate.repository, [gate]);
  }
  return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right, "de", { sensitivity: "base" }));
}

function flowLabel(stage: string): string {
  return ({
    start_required: "Start erforderlich",
    base_update_required: "Branch aktualisieren",
    review_pending: "Review läuft",
    review_fix_required: "Fix erforderlich",
    ci_pending: "CI läuft",
    approval_required: "Freigabe erforderlich",
    ready_to_merge: "Ready for Merge",
    action_required: "Prüfung erforderlich",
    waiting: "Wartet",
    success: "Ready for Merge",
    failure: "Fehler",
    clean: "sauber",
    pending: "ausstehend",
    changes_requested: "Fix erforderlich",
  } as Record<string, string>)[stage] ?? stage;
}

function flowStep(stage: string): number {
  if (stage === "start_required") return 1;
  if (["approval_required"].includes(stage)) return 3;
  if (["ready_to_merge", "success"].includes(stage)) return 4;
  return 2;
}

function FlowProgress({ stage }: { stage: string }) {
  const current = flowStep(stage);
  const labels = ["Start", "CI & Review", "Freigabe", "Merge"];
  return <div className="flow-progress" aria-label={`Schritt ${current} von 4: ${labels[current - 1]}`}>
    {labels.map((label, index) => <span key={label} className={index + 1 < current ? "done" : index + 1 === current ? "current" : ""}><i />{label}</span>)}
  </div>;
}

function GithubView({ items, operator, busy, act, reload }: any) {
  const [filter, setFilter] = useState<GateFilter>("all");
  const [repo, setRepo] = useState<string | null>(null);
  const [selected, setSelected] = useState<Gate | null>(null);
  const [details, setDetails] = useState<PullRequestDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const detailsRequest = useRef(0);
  const backButton = useRef<HTMLButtonElement | null>(null);
  const previousRepo = useRef<string | null>(null);
  const groups = groupGatesByRepository(items, filter);
  const scope = repo ? items.filter((gate: Gate) => gate.repository === repo) : items;
  const repoGates = repo ? groups.find(([repository]) => repository === repo)?.[1] ?? [] : [];
  const count = (status: GateFilter) => status === "all" ? scope.length : scope.filter((gate: Gate) => FLOW_FILTERS[status].includes(gate.gateStatus)).length;
  useEffect(() => {
    if (repo && !items.some((gate: Gate) => gate.repository === repo)) setRepo(null);
  }, [repo, items]);
  useEffect(() => {
    if (repo) backButton.current?.focus();
    else if (previousRepo.current) {
      const card = document.querySelector<HTMLButtonElement>(`.repo-card[data-repo="${CSS.escape(previousRepo.current)}"]`)
        ?? document.querySelector<HTMLButtonElement>(".repo-card");
      (card ?? document.querySelector<HTMLElement>(".github-view .empty"))?.focus();
    }
    previousRepo.current = repo;
  }, [repo]);
  const openDetails = useCallback(async (gate: Gate, silent = false) => {
    const request = ++detailsRequest.current;
    setSelected(gate);
    if (!silent) { setDetails(null); setDetailsError(null); setDetailsLoading(true); }
    try {
      const query = new URLSearchParams({ repository: gate.repository, pullNumber: String(gate.pullNumber) });
      const result = await api<PullRequestDetails>(`/api/github/gates/details?${query}`);
      if (request === detailsRequest.current) {
        setDetails(result);
        setDetailsError(null);
      }
    } catch (current) {
      if (request === detailsRequest.current) setDetailsError(current instanceof Error ? current.message : "Pull Request konnte nicht geladen werden");
    } finally {
      if (!silent && request === detailsRequest.current) setDetailsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!selected) return;
    const timer = window.setInterval(() => void openDetails(selected, true), 15_000);
    return () => window.clearInterval(timer);
  }, [selected, openDetails]);
  const closeDetails = () => {
    detailsRequest.current += 1;
    setSelected(null); setDetails(null); setDetailsError(null); setDetailsLoading(false);
  };
  const runAction = async (action: NonNullable<PullRequestDetails["nextAction"]>) => {
    if (!selected) return;
    const id = `github:${selected.repository}#${selected.pullNumber}:${action}`;
    const target = { repository: selected.repository, pullNumber: selected.pullNumber };
    await act(
      id,
      () => api(`/api/github/gates/${action}`, { method: "POST", body: JSON.stringify(target) }),
      action === "start" ? "Prüfung wurde gestartet" : action === "approve" ? "Freigabe wird verarbeitet" : "Pull Request wurde gemergt",
      async () => {
        if (action === "merge") closeDetails();
        await reload();
        if (action !== "merge") await openDetails(selected);
      },
    );
  };
  const filterLabels: Record<GateFilter, string> = {
    all: "Alle", start: "Start", review: "Prüfung", fix: "Fix nötig", approval: "Freigabe", merge: "Merge", failure: "Fehler",
  };
  return <section className="github-view"><PageHeading eyebrow="Merge Control" title="GitHub Pull-Request-Flows" description="Prüfung, Review, Freigabe und Merge pro aktuellem Commit." />
    {!!items.length && !repo && <div className="section-heading"><span>{items.length} offene Pull Requests</span></div>}
    {repo && <Button ref={backButton} appearance="subtle" icon={<ArrowLeft20Regular />} className="repo-back" onClick={() => setRepo(null)}>Alle Repositories</Button>}
    {repo && <div className="section-heading"><div><h2>{repo}</h2><p>{scope.length} {scope.length === 1 ? "offener Pull Request" : "offene Pull Requests"}</p></div></div>}
    {!!items.length && <TabList size="small" selectedValue={filter} onTabSelect={(_, data) => setFilter(data.value as GateFilter)} className="github-filters">
      {(Object.keys(filterLabels) as GateFilter[]).map((status) => <Tab key={status} value={status}>{filterLabels[status]} <Badge size="small">{count(status)}</Badge></Tab>)}
    </TabList>}
    {!items.length ? <Empty>Keine offenen Pull Requests vorhanden.</Empty> : repo
      ? (!repoGates.length ? <Empty>Keine Pull Requests für diesen Filter.</Empty> : <div className="data-list">{repoGates.map((gate) => <article className="data-row github-flow-row" key={`${gate.repository}#${gate.pullNumber}`}>
        <span aria-hidden="true" className={`signal ${["ready_to_merge", "success"].includes(gate.gateStatus) ? "info" : ["failure", "base_update_required"].includes(gate.gateStatus) ? "critical" : "warning"}`} />
        <div className="grow"><div className="row-title"><button type="button" className="pr-title-button" onClick={() => void openDetails(gate)}><strong>#{gate.pullNumber} · {gate.title}</strong></button><Status value={gate.gateStatus} label={flowLabel(gate.gateStatus)} /></div><p>{gate.author} → {gate.baseBranch} · {gate.gateSummary}</p><FlowProgress stage={gate.gateStatus} /></div>
      </article>)}</div>)
      : !groups.length ? <Empty>Keine Pull Requests für diesen Filter.</Empty> : <div className="card-grid">{groups.map(([repository, gates]) => {
        const failures = gates.filter((gate) => ["failure", "base_update_required"].includes(gate.gateStatus)).length;
        const ready = gates.filter((gate) => ["ready_to_merge", "success"].includes(gate.gateStatus)).length;
        const active = gates.length - failures - ready;
        const slash = repository.indexOf("/");
        return <button type="button" key={repository} className="repo-card" data-repo={repository} onClick={() => setRepo(repository)}>
          <div className="repo-card-top"><div>{slash > 0 && <small>{repository.slice(0, slash)}</small>}<strong>{slash > 0 ? repository.slice(slash + 1) : repository}</strong></div><Badge appearance="tint">{gates.length} {gates.length === 1 ? "PR" : "PRs"}</Badge></div>
          <div className="repo-chips">
            {failures > 0 && <Badge size="small" appearance="tint" color="danger">{failures} Fehler</Badge>}
            {active > 0 && <Badge size="small" appearance="tint" color="warning">{active} in Arbeit</Badge>}
            {ready > 0 && <Badge size="small" appearance="tint" color="success">{ready} Ready for Merge</Badge>}
          </div>
        </button>;
      })}</div>}
    <PullRequestDialog gate={selected} details={details} loading={detailsLoading} error={detailsError} operator={operator} busy={busy} action={runAction} close={closeDetails} />
  </section>;
}

function PullRequestDialog({ gate, details, loading, error, operator, busy, action, close }: {
  gate: Gate | null; details: PullRequestDetails | null; loading: boolean; error: string | null;
  operator: boolean; busy: string | null; action: (value: NonNullable<PullRequestDetails["nextAction"]>) => Promise<void>; close: () => void;
}) {
  const actionLabel = details?.nextAction === "start" ? "Prüfung starten"
    : details?.nextAction === "approve" ? "Freigeben"
      : details?.nextAction === "merge" ? "Ready for Merge" : null;
  return <Dialog open={Boolean(gate)} onOpenChange={(_, data) => { if (!data.open) close(); }}>
    <DialogSurface className="pr-dialog">
      <DialogBody>
        <DialogTitle>{gate ? `${gate.repository} #${gate.pullNumber}` : "Pull Request"}</DialogTitle>
        <DialogContent>
          {loading && <Spinner label="Pull Request wird von GitHub geladen …" />}
          {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
          {details && <div className="pr-details">
            <div className="pr-detail-heading"><div><h2>{details.title}</h2><p>{details.author} · {details.headBranch} → {details.baseBranch}</p><small>Erstellt {relative(details.createdAt)} · aktualisiert {relative(details.updatedAt)} · <code>H {details.headSha.slice(0, 7)} / B {details.baseSha.slice(0, 7)}</code></small></div><div className="actions"><Status value={details.draft ? "draft" : details.state} /><Status value={details.mergeableState} /></div></div>
            <section className="flow-panel">
              <div><strong>{flowLabel(details.flowStage)}</strong><p>{details.flowSummary}</p></div>
              <FlowProgress stage={details.flowStage} />
            </section>
            <div className="pr-metrics">
              <span><strong>{details.commits}</strong> Commits</span><span><strong>{details.changedFiles}</strong> Dateien</span>
              <span className="additions">+{details.additions}</span><span className="deletions">−{details.deletions}</span>
              <span>{details.comments + details.reviewComments} Kommentare</span>
            </div>
            <section className="pr-detail-section"><h3>Beschreibung</h3><p className="pr-body">{details.body || "Keine Beschreibung vorhanden."}</p></section>
            <section className="pr-detail-section"><h3>Checks ({details.checks.length}{details.checksTruncated ? "+" : ""})</h3>
              {details.checksTruncated && <p>Die ersten 100 Checks werden angezeigt.</p>}
              {!details.checks.length ? <p>Keine Checks vorhanden.</p> : <div className="compact-list">{details.checks.map((check) => <div key={check.id}><span>{check.detailsUrl ? <a href={check.detailsUrl} target="_blank" rel="noreferrer">{check.name}</a> : check.name}</span><Status value={check.conclusion ?? check.status} /></div>)}</div>}
            </section>
            {!!details.reviewFeedback.length && <section className="pr-detail-section"><h3>Offenes {details.reviewerName ?? "Review"}-Feedback ({details.reviewFeedback.length})</h3>
              <div className="review-feedback">{details.reviewFeedback.map((feedback, index) => <p key={index}>{feedback}</p>)}</div>
            </section>}
            <section className="pr-detail-section"><h3>Reviews ({details.reviews.length}{details.reviewsTruncated ? "+" : ""})</h3>
              <p>{details.reviewerName ?? "Review"}: {flowLabel(details.codexReviewState)}{details.codexReviewSha ? ` · ${details.codexReviewSha}` : ""}</p>
              {!!details.requestedReviewers.length && <p>Angefragt: {details.requestedReviewers.join(", ")}</p>}
              {details.reviewsTruncated && <p>Die ersten 100 Reviews werden angezeigt.</p>}
              {!details.reviews.length ? <p>Noch keine Reviews vorhanden.</p> : <div className="compact-list">{details.reviews.map((review) => <div key={review.id}><span>{review.author}{review.submittedAt ? <small> · {relative(review.submittedAt)}</small> : null}</span><Status value={review.state.toLowerCase()} /></div>)}</div>}
            </section>
            {(details.labels.length > 0 || details.assignees.length > 0) && <section className="pr-detail-section"><h3>Zuordnung</h3><p>{details.labels.length ? `Labels: ${details.labels.join(", ")}` : ""}{details.labels.length && details.assignees.length ? " · " : ""}{details.assignees.length ? `Assignees: ${details.assignees.join(", ")}` : ""}</p></section>}
          </div>}
        </DialogContent>
        <DialogActions>
          {gate && <Button as="a" href={details?.url ?? gate.url} target="_blank" rel="noreferrer">Auf GitHub öffnen</Button>}
          {actionLabel && details?.nextAction && <Button appearance="primary" icon={<Checkmark20Regular />} disabled={!operator || Boolean(busy)} onClick={() => void action(details.nextAction!)}>{actionLabel}</Button>}
          <Button onClick={close}>Schließen</Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  </Dialog>;
}

function RepositoriesView({ jobs, repositories, branchTips, operator, busy, act, reload }: {
  jobs: OnboardingJob[]; repositories: ManagedRepository[]; branchTips: ProtectedBranchTip[]; operator: boolean; busy: string | null;
  act: (id: string, action: () => Promise<unknown>, success: string, refresh?: () => Promise<void>) => Promise<void>;
  reload: () => Promise<void>;
}) {
  const [form, setForm] = useState({ repositoryName: "", description: "", team: "", teamPermission: "maintain", topics: "" });
  const submit = (job?: OnboardingJob) => act(
    job ? `retry-${job.id}` : "onboard",
    () => api("/api/github/onboarding", {
      method: "POST",
      body: JSON.stringify(job ? {
        repositoryName: job.repository.split("/")[1], profile: job.profile, description: job.description, team: job.team,
        teamPermission: job.teamPermission, topics: job.topics,
      } : {
        repositoryName: form.repositoryName, profile: "base", description: form.description, team: form.team, teamPermission: form.teamPermission,
        topics: form.topics.split(",").map((topic) => topic.trim().toLowerCase()).filter(Boolean),
      }),
    }),
    job ? "Onboarding erneut eingeplant" : "Onboarding eingeplant",
    reload,
  );
  return <section><PageHeading eyebrow="Repository Governance" title="Onboarding und Blueprint-Drift" description="Zentrale Aufträge, Klassifikation und verwaltete Blueprint-Stände." />
    <form className="inline-form repository-form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <Input required aria-label="Repository-Name" placeholder="Repository-Name" value={form.repositoryName} onChange={(_, data) => setForm({ ...form, repositoryName: data.value })} />
      <Input required aria-label="Beschreibung" placeholder="Beschreibung" value={form.description} onChange={(_, data) => setForm({ ...form, description: data.value })} />
      <Input required aria-label="Team" placeholder="team-slug" value={form.team} onChange={(_, data) => setForm({ ...form, team: data.value })} />
      <Select aria-label="Team-Berechtigung" value={form.teamPermission} onChange={(_, data) => setForm({ ...form, teamPermission: data.value })}>
        <option value="pull">Lesen</option><option value="triage">Triage</option><option value="push">Schreiben</option>
        <option value="maintain">Verwalten</option><option value="admin">Administration</option>
      </Select>
      <Input required aria-label="Topics" placeholder="topic-a, topic-b" value={form.topics} onChange={(_, data) => setForm({ ...form, topics: data.value })} />
      <Button type="submit" appearance="primary" disabled={!operator || busy === "onboard"}>Repository onboarden</Button>
    </form>
    <div className="section-heading"><div><h2>Aufträge</h2><p>Fortschritt und sichere Repository-Klassifikation.</p></div></div>
    {!jobs.length ? <Empty>Noch keine Onboarding-Aufträge vorhanden.</Empty> : <div className="data-list">{jobs.map((job) => <article className="data-row action-row" key={job.id}>
      <div className="grow"><div className="row-title"><strong>{job.repository}</strong><Status value={job.status} /></div>
        <p>{job.classification ?? "noch nicht klassifiziert"} · Schritt {job.currentStep} · {job.profile}/{job.capabilities.join(", ")} · Team {job.team} ({job.teamPermission})</p>
        {job.migrationBaselines?.map((baseline) => <p key={baseline.id}>
          Baseline {baseline.route} · {baseline.status} · <code>{baseline.sourceSha.slice(0, 7)} / {baseline.targetSha.slice(0, 7)}</code>
          {baseline.invalidReason ? ` · ${baseline.invalidReason}` : ""}
          {baseline.pullNumber ? <> · <a href={`https://github.com/${job.repository}/pull/${baseline.pullNumber}`} target="_blank" rel="noreferrer">PR #{baseline.pullNumber}</a></> : null}
        </p>)}
        {job.error && <p>{job.error}</p>}
      </div>
      <div className="actions">{job.status === "failed" && <Button size="small" disabled={!operator || busy === `retry-${job.id}`} onClick={() => void submit(job)}>Erneut versuchen</Button>}</div>
    </article>)}</div>}
    <div className="section-heading repository-status-heading"><div><h2>Verwaltete Repositories</h2><p>Gewünschter und angewendeter unveränderlicher Blueprint-Commit.</p></div></div>
    {!repositories.length ? <Empty>Noch keine verwalteten Repositories inventarisiert.</Empty> : <div className="data-list">{repositories.map((repository) => <article className="data-row action-row" key={repository.repository}>
      <div className="grow"><div className="row-title"><strong>{repository.repository}</strong><Status value={repository.status} /></div>
        <p>{repository.profile}/{repository.capabilities.join(", ")} · Soll {repository.desiredBlueprintCommit.slice(0, 10)} · Ist {repository.appliedBlueprintCommit?.slice(0, 10) ?? "offen"}</p>
        {branchTips.filter((tip) => tip.repository.toLowerCase() === repository.repository.toLowerCase()).map((tip) => <p key={tip.branch}>
          {tip.branch} · <Status value={tip.status} /> · <code>{tip.tipSha.slice(0, 10)}</code>
          {tip.status === "deleted" && <Button size="small" disabled={!operator || busy === `recover-${repository.repository}-${tip.branch}`} onClick={() => void act(
            `recover-${repository.repository}-${tip.branch}`,
            () => api("/api/github/protected-branches/recover", { method: "POST", body: JSON.stringify({ repository: repository.repository, branch: tip.branch }) }),
            `${tip.branch} Wiederherstellung angefordert`, reload,
          )}>Wiederherstellen</Button>}
          {tip.status === "blocked" && " · separate auditierte Incident-Bereinigung erforderlich"}
        </p>)}
        {!!repository.drift.length && <p>Drift: {repository.drift.join(", ")}</p>}
        {repository.lastError && <p>{repository.lastError}</p>}
      </div>
      {repository.updatePullNumber && <Button as="a" size="small" href={`https://github.com/${repository.repository}/pull/${repository.updatePullNumber}`} target="_blank" rel="noreferrer">Update-PR #{repository.updatePullNumber}</Button>}
    </article>)}</div>}
  </section>;
}

function BreakglassView({ data, operator, busy, act }: any) {
  const [view, setView] = useState("sessions");
  const [technician, setTechnician] = useState({ name: "", email: "", unixUsername: "" });
  const [hostname, setHostname] = useState("");
  const sessions = data?.sessions ?? [];
  // Without loaded data (not configured, e.g. InfraTeams Dev, or not loaded) there is nothing to show or create.
  if (!data) return <section><PageHeading eyebrow="Emergency Access" title="Breaking Glass" description="FIDO2-geschützte, zeitlich begrenzte Serverzugriffe." />
    <MessageBar intent="info"><MessageBarBody>Breaking Glass ist in dieser Umgebung nicht konfiguriert oder wurde nicht geladen.</MessageBarBody></MessageBar>
  </section>;
  return <section><PageHeading eyebrow="Emergency Access" title="Breaking Glass" description="FIDO2-geschützte, zeitlich begrenzte Serverzugriffe." />
    <TabList selectedValue={view} onTabSelect={(_, info) => setView(String(info.value))} className="subtabs">
      <Tab value="sessions">Sessions <Badge size="small" color="danger">{sessions.filter((item: any) => item.status === "pending").length}</Badge></Tab>
      <Tab value="technicians">Techniker</Tab><Tab value="servers">Server</Tab><Tab value="audit">Audit-Log</Tab>
    </TabList>
    {view === "sessions" && (!sessions.length ? <Empty>Keine Breaking-Glass-Sessions vorhanden.</Empty> : <div className="card-grid">{sessions.map((session: any) => <Card key={session.id} className="session-card">
      <div className="row-title"><div className="person"><span className="avatar">{String(session.technician_name).split(" ").map((part) => part[0]).slice(0, 2).join("")}</span><div><strong>{session.technician_name}</strong><small>{session.unix_username}</small></div></div><Status value={session.status} /></div>
      <dl><div><dt>Server</dt><dd>{session.hostname}</dd></div><div><dt>Ticket</dt><dd>{session.ticket_id}</dd></div><div><dt>Angefragt</dt><dd>{relative(session.created_at)}</dd></div></dl>
      {session.status === "pending" && <div className="actions"><Button appearance="primary" disabled={!operator || busy === session.id} onClick={() => act(session.id, () => api("/api/breakglass/sessions/decision", { method: "POST", body: JSON.stringify({ sessionId: session.id, decision: "approved" }) }), "Zugang genehmigt")}>Genehmigen</Button><Button disabled={!operator || busy === session.id} onClick={() => act(session.id, () => api("/api/breakglass/sessions/decision", { method: "POST", body: JSON.stringify({ sessionId: session.id, decision: "denied" }) }), "Anfrage abgelehnt")}>Ablehnen</Button></div>}
    </Card>)}</div>)}
    {view === "technicians" && <>
      <form className="inline-form" onSubmit={(event) => { event.preventDefault(); void act("create-tech", () => api("/api/breakglass/technicians", { method: "POST", body: JSON.stringify(technician) }), "Techniker angelegt").then(() => setTechnician({ name: "", email: "", unixUsername: "" })); }}>
        <Input required aria-label="Name" placeholder="Name" value={technician.name} onChange={(_, info) => setTechnician({ ...technician, name: info.value })} />
        <Input required type="email" aria-label="E-Mail" placeholder="E-Mail" value={technician.email} onChange={(_, info) => setTechnician({ ...technician, email: info.value })} />
        <Input required pattern="adm_ext_.*" aria-label="Unix-Username" placeholder="adm_ext_username" value={technician.unixUsername} onChange={(_, info) => setTechnician({ ...technician, unixUsername: info.value })} />
        <Button type="submit" appearance="primary" disabled={!operator || busy === "create-tech"}>Techniker anlegen</Button>
      </form>
      <TechnicianTable rows={data?.technicians ?? []} operator={operator} busy={busy} act={act} />
    </>}
    {view === "servers" && <>
      <form className="inline-form server-form" onSubmit={(event) => { event.preventDefault(); void act("create-server", () => api("/api/breakglass/servers", { method: "POST", body: JSON.stringify({ hostname }) }), "Server angelegt").then(() => setHostname("")); }}>
        <Input required aria-label="Hostname" placeholder="server.example.internal" value={hostname} onChange={(_, info) => setHostname(info.value)} />
        <Button type="submit" appearance="primary" disabled={!operator || busy === "create-server"}>Server anlegen</Button>
      </form>
      <SimpleTable columns={["Hostname", "Server-ID", "Angelegt"]} rows={(data?.servers ?? []).map((item: any) => [item.hostname, item.id, item.created_at])} />
    </>}
    {view === "audit" && <SimpleTable columns={["Zeit", "Ereignis", "Techniker", "Server", "Details"]} rows={(data?.audit ?? []).map((item: any) => [item.created_at, item.event_type, item.technician_name ?? "—", item.hostname ?? "—", item.reason ?? "—"])} />}
  </section>;
}

function TechnicianTable({ rows, operator, busy, act }: any) {
  if (!rows.length) return <Empty>Keine Techniker vorhanden.</Empty>;
  return <div className="table-wrap"><table><thead><tr><th>Name</th><th>Unix-Username</th><th>Geräte</th><th>Status</th><th>Aktion</th></tr></thead><tbody>{rows.map((item: any) => <tr key={item.id}>
    <td>{item.name}<br /><small>{item.email}</small></td><td>{item.unix_username}</td><td>{item.active_devices}</td><td><Status value={item.revoked ? "Gesperrt" : "Aktiv"} /></td>
    <td><div className="actions"><Button size="small" disabled={!operator || item.revoked || busy === `invite-${item.id}`} onClick={() => act(`invite-${item.id}`, () => api("/api/breakglass/technicians/invite", { method: "POST", body: JSON.stringify({ technicianId: item.id }) }), "Einladung versendet")}>Einladen</Button><Button size="small" disabled={!operator || item.revoked || busy === `revoke-${item.id}`} onClick={() => act(`revoke-${item.id}`, () => api("/api/breakglass/technicians/revoke", { method: "POST", body: JSON.stringify({ technicianId: item.id }) }), "Techniker gesperrt")}>Sperren</Button></div></td>
  </tr>)}</tbody></table></div>;
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: unknown[][] }) {
  if (!rows.length) return <Empty>Keine Einträge vorhanden.</Empty>;
  return <div className="table-wrap"><table><thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{String(cell)}</td>)}</tr>)}</tbody></table></div>;
}

function PageHeading({ eyebrow, title, description, search, setSearch }: { eyebrow: string; title: string; description: string; search?: string; setSearch?: (value: string) => void }) {
  return <div className="page-heading"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div>{setSearch && <Input aria-label="Liste durchsuchen" placeholder="Suchen …" value={search} onChange={(_, data) => setSearch(data.value)} />}</div>;
}
